/**
 * Device Types Configuration - Frontend
 * @description Flexible configuration for device vehicle types
 * This file allows easy modification of vehicle types without code changes
 */

import deviceTypesJson from './deviceTypes.json';

export interface DeviceTypeConfig {
  value: string;
  labels: {
    en: string;
    es: string;
  };
  descriptions: {
    en: string;
    es: string;
  };
}

export interface DeviceTypesConfig {
  defaultType: string;
  types: DeviceTypeConfig[];
}

// Load configuration from JSON file
export const deviceTypesConfig: DeviceTypesConfig = deviceTypesJson;

/**
 * Get all available vehicle types
 */
export const getVehicleTypes = (): string[] => {
  return deviceTypesConfig.types.map((type) => type.value);
};

/**
 * Get vehicle type configuration by value
 */
export const getVehicleTypeConfig = (value: string): DeviceTypeConfig | undefined => {
  return deviceTypesConfig.types.find((type) => type.value === value);
};

/**
 * Get vehicle type label by value and language
 */
export const getVehicleTypeLabel = (value: string, language: 'en' | 'es' = 'en'): string => {
  const config = getVehicleTypeConfig(value);
  return config?.labels[language] || value;
};

/**
 * Get vehicle type description by value and language
 */
export const getVehicleTypeDescription = (value: string, language: 'en' | 'es' = 'en'): string => {
  const config = getVehicleTypeConfig(value);
  return config?.descriptions[language] || '';
};

/**
 * Get default vehicle type
 */
export const getDefaultVehicleType = (): string => {
  return deviceTypesConfig.defaultType;
};

/**
 * Check if a vehicle type is valid
 */
export const isValidVehicleType = (type: string): boolean => {
  return deviceTypesConfig.types.some((config) => config.value === type);
};

/**
 * Resolve API / form value to a configured enum (case-insensitive), if any.
 */
export const resolveVehicleTypeKey = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const byExact = deviceTypesConfig.types.find((t) => t.value === trimmed);
  if (byExact) return byExact.value;
  const upper = trimmed.toUpperCase();
  return deviceTypesConfig.types.find((t) => t.value.toUpperCase() === upper)?.value;
};

/**
 * Human-readable vehicle type for tables, exports, and filters.
 * - Known enums → label from deviceTypes.json
 * - OTHER → "Other" / "Otro"
 * - Custom free text (or legacy values) → "Other — …" / "Otro — …"
 * - If the value is already a translated label (e.g. filter second pass), returns it as-is.
 */
export const formatVehicleTypeForDisplay = (
  type: string | undefined | null,
  language: 'en' | 'es' = 'en'
): string => {
  const raw = (type ?? '').trim();
  if (!raw) return '';

  const resolved = resolveVehicleTypeKey(raw);
  if (resolved && resolved !== 'OTHER') {
    return getVehicleTypeLabel(resolved, language);
  }
  if (resolved === 'OTHER') {
    return getVehicleTypeLabel('OTHER', language);
  }

  const alreadyLabel = deviceTypesConfig.types.some(
    (t) => t.labels.en === raw || t.labels.es === raw
  );
  if (alreadyLabel) {
    return raw;
  }

  const otherLabel = getVehicleTypeLabel('OTHER', language);
  return `${otherLabel} — ${raw}`;
};
